import { useState, useRef, useEffect } from "react";
import { API } from "../App";
import styles from "./VisionPanel.module.css";

export default function VisionPanel({ showToast }) {

  const [sourceTab, setSourceTab] = useState("upload");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [messages, setMessages] = useState([]);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (sourceTab === "camera") startCamera();
    else stopCamera();
    return () => stopCamera();
  }, [sourceTab]);

  const startCamera = async () => {
    try {

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setCaptured(false);
      setSelectedFile(null);
      setPreviewUrl("");

    } catch {

      showToast("Camera access denied", "error");
      setSourceTab("upload");

    }
  };

  const stopCamera = () => {

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

  };

  const capturePhoto = () => {

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");

    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {

      const file = new File([blob], "capture.jpg", { type: "image/jpeg" });

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(blob));
      setCaptured(true);

      stopCamera();

      showToast("Photo captured", "success");

    }, "image/jpeg", 0.9);
  };

  const retake = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setCaptured(false);
    startCamera();
  };

  const handleFileSelect = (e) => {

    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please select an image", "error");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {

    e.preventDefault();

    const file = e.dataTransfer.files[0];

    if (file && file.type.startsWith("image/")) {

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));

    } else {

      showToast("Please drop an image file", "error");

    }
  };

  const analyze = async () => {

    if (!selectedFile) return;

    const savedPreview = previewUrl;
    const savedNote = note;

    setMessages(prev => [
      ...prev,
      { role: "user", previewUrl: savedPreview, note: savedNote || null }
    ]);

    setLoading(true);

    const fd = new FormData();
    fd.append("file", selectedFile);
    fd.append("note", note);

    try {

      const res = await fetch(`${API}/vision`, {
        method: "POST",
        body: fd
      });

      if (!res.ok) throw new Error();

      const data = await res.json();

      setMessages(prev => [
        ...prev,
        { role: "ai", text: data.analysis }
      ]);

      setSelectedFile(null);
      setPreviewUrl("");
      setNote("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (sourceTab === "camera") startCamera();

    } catch {

      showToast("Server error — check backend connection", "error");

    } finally {

      setLoading(false);

    }
  };

  const clearAll = () => {

    setMessages([]);
    setSelectedFile(null);
    setPreviewUrl("");
    setNote("");
    setCaptured(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (sourceTab === "camera") startCamera();
  };

  return (
    <div className={styles.panel}>

      <div className={styles.header}>
        <div>
          <h2>Image Analysis</h2>
          <p>Upload or capture a medical image for AI‑assisted assessment</p>
        </div>

        {messages.length > 0 && (
          <button className={styles.clearBtn} onClick={clearAll}>
            Clear Chat
          </button>
        )}
      </div>

      {messages.length > 0 && (

        <div className={styles.chatBox}>

          {messages.map((msg, i) => (

            <div key={i} className={msg.role === "user" ? styles.userMsg : styles.aiMsg}>

              <div className={styles.msgLabel}>
                {msg.role === "user" ? "YOU" : "AIDORA"}
              </div>

              {msg.role === "user" ? (

                <div className={styles.userContent}>
                  <img src={msg.previewUrl} alt="upload" className={styles.chatImg}/>
                  {msg.note && <p className={styles.chatNote}>{msg.note}</p>}
                </div>

              ) : (

                <div className={styles.msgText}>{msg.text}</div>

              )}

            </div>

          ))}

          {loading && (

            <div className={styles.aiMsg}>
              <div className={styles.msgLabel}>AIDORA</div>
              <div className={styles.typing}><span/><span/><span/></div>
            </div>

          )}

          <div ref={bottomRef}/>

        </div>
      )}

      <div className={styles.tabs}>

        <button
          className={`${styles.tab} ${sourceTab === "upload" ? styles.activeTab : ""}`}
          onClick={() => setSourceTab("upload")}
        >
          Upload File
        </button>

        <button
          className={`${styles.tab} ${sourceTab === "camera" ? styles.activeTab : ""}`}
          onClick={() => setSourceTab("camera")}
        >
          Take Photo
        </button>

      </div>

      {sourceTab === "upload" && (

        <div
          className={styles.uploadZone}
          onClick={() => !previewUrl && fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          {previewUrl ? (
            <img src={previewUrl} alt="preview" className={styles.preview}/>
          ) : (
            <span>Click or drag an image here</span>
          )}

        </div>

      )}

      {sourceTab === "camera" && (

        <div className={styles.cameraZone}>

          {!captured ? (

            <>
              <video ref={videoRef} autoPlay playsInline className={styles.video}/>
              <button className={styles.captureBtn} onClick={capturePhoto}>Capture</button>
            </>

          ) : (

            <div className={styles.capturedWrap}>
              <img src={previewUrl} alt="capture" className={styles.capturedImg}/>
              <button onClick={retake}>Retake</button>
            </div>

          )}

          <canvas ref={canvasRef} style={{ display: "none" }}/>

        </div>

      )}

      <input
        type="text"
        placeholder="Optional note about the image"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className={styles.noteInput}
      />

      <div className={styles.actions}>

        <button
          className={styles.btnPrimary}
          onClick={analyze}
          disabled={!selectedFile || loading}
        >
          {loading ? "Analyzing..." : "Analyze Image"}
        </button>

      </div>

    </div>
  );
}